import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import IosShareIcon from '@mui/icons-material/IosShare';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('App is already installed');
      return;
    }

    // Check if iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    // Check if Android
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS && !window.navigator.standalone) {
      // Show iOS install prompt after delay
      setTimeout(() => {
        const hasSeenIOSPrompt = localStorage.getItem('hasSeenIOSPrompt');
        if (!hasSeenIOSPrompt && isMobile) {
          setShowIOSPrompt(true);
        }
      }, 5000);
    }

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after delay
      setTimeout(() => {
        const hasSeenPrompt = localStorage.getItem('hasSeenInstallPrompt');
        if (!hasSeenPrompt && isMobile) {
          setShowInstallPrompt(true);
        }
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isMobile]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setShowSuccess(true);
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
    localStorage.setItem('hasSeenInstallPrompt', 'true');
  };

  const handleClosePrompt = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('hasSeenInstallPrompt', 'true');
  };

  const handleCloseIOSPrompt = () => {
    setShowIOSPrompt(false);
    localStorage.setItem('hasSeenIOSPrompt', 'true');
  };

  // Android/Chrome Install Dialog
  const AndroidInstallDialog = () => (
    <Dialog 
      open={showInstallPrompt} 
      onClose={handleClosePrompt}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="span">
            Install Carline App
          </Typography>
          <IconButton onClick={handleClosePrompt} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box textAlign="center" py={2}>
          <AddToHomeScreenIcon sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            Install the Carline app for quick access from your home screen!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Works offline
            • Faster loading
            • Easy one-tap access
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={handleClosePrompt} color="inherit">
          Not Now
        </Button>
        <Button 
          onClick={handleInstallClick} 
          variant="contained"
          startIcon={<AddToHomeScreenIcon />}
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            px: 3
          }}
        >
          Install
        </Button>
      </DialogActions>
    </Dialog>
  );

  // iOS Install Instructions
  const IOSInstallDialog = () => (
    <Dialog 
      open={showIOSPrompt} 
      onClose={handleCloseIOSPrompt}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="span">
            Add to Home Screen
          </Typography>
          <IconButton onClick={handleCloseIOSPrompt} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={2}>
          <Typography variant="body1" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
            Install the Carline app for easy access!
          </Typography>
          
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              1. Tap the share button <IosShareIcon sx={{ mx: 1, color: '#007AFF' }} /> at the bottom
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              2. Scroll down and tap "Add to Home Screen"
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              3. Tap "Add" in the top right corner
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
            You'll get instant access to Carline from your home screen!
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={handleCloseIOSPrompt} variant="contained" sx={{ px: 4 }}>
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <AndroidInstallDialog />
      <IOSInstallDialog />
      
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={6000} 
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          App installed successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstallPrompt;